import { Recommendation, TradeSuggestionsItem } from '@/types/client';
import { formatPrice as formatPriceWithCurrency } from '@/utils/formatters';
import { isGlobalCountryCode } from '@/utils/country';
import {
  computeImpliedReturn,
  priceForIr,
  yearsToTarget,
} from '@/components/order_recommendation/recommendationSelectors';
import type { PillTone } from '@/components/ui';

/** What every cell shows when there is nothing to show. Matches the formatters. */
export const DASH = '—';

// Separator used to encode strategy sort keys as a single string.
// Must not appear in strategy codes.
export const STRATEGY_SORT_SEP = '___' as const;

// India ("IN") is Domestic; any other code is Global. Global rows append the ISO
// currency code in brackets, e.g. "100.00 (USD)"; Domestic rows show plain numbers.
export const isGlobalItem = (item: TradeSuggestionsItem): boolean =>
  isGlobalCountryCode(item.country_code);

// Table cells show no symbol on Domestic rows (domesticSymbol="").
export const formatPrice = (value: number, code: string | null | undefined, isGlobal: boolean): string =>
  formatPriceWithCurrency(value, code, isGlobal, '');

// Recommendation prices carry raw's currency_code, the LTP carries the live market data's
// ltp_currency_code. When they differ, compounding a target against a CMP quoted in another
// currency is meaningless, so the implied return is withheld rather than shown wrong.
const hasCurrencyMismatch = (item: TradeSuggestionsItem): boolean =>
  item.currency_code != null &&
  item.ltp_currency_code != null &&
  item.currency_code !== item.ltp_currency_code;

/** The price the implied return compounds to — target price, or the decision price on a
 *  "reduce above" decision. Delegates to the shared selector via a narrowed object literal:
 *  `Recommendation`'s suggestions map is not assignable to the selector's index-signature
 *  type, and only these three fields decide the target. */
export const targetForIr = (rec: Recommendation | null): number | null =>
  rec == null
    ? null
    : priceForIr({
      decision: rec.decision,
      target_price: rec.target_price,
      decision_price: rec.decision_price,
    });

/**
 * Annualised implied return % for one recommendation row: ((Target / LTP) ^ (1 / years)) - 1.
 *
 * Deliberately built from the Orders & Recommendations selectors rather than a local formula,
 * so both screens resolve the same target price ("reduce above" decisions price off the
 * decision price) and quote the same figure for the same recommendation. Null when there is
 * no LTP, no future target date, or a currency mismatch.
 */
export const rowImpliedReturn = (
  item: TradeSuggestionsItem,
  rec: Recommendation | null,
  refDate: Date,
): number | null => {
  if (!rec || hasCurrencyMismatch(item)) return null;
  return computeImpliedReturn(targetForIr(rec), item.ltp, yearsToTarget(rec.target_date, refDate));
};

/** Why an implied return could not be computed, shown on the blank cell so a missing
 *  figure is never mistaken for a zero return. */
export const impliedReturnBlankReason = (
  item: TradeSuggestionsItem,
  rec: Recommendation | null,
  refDate: Date,
): string => {
  if (!rec) return 'No recommendation';
  if (hasCurrencyMismatch(item)) {
    return `Recommendation currency (${item.currency_code}) differs from LTP currency (${item.ltp_currency_code})`;
  }
  if (item.ltp == null) return 'No LTP';
  if (targetForIr(rec) == null) return 'No target price';
  if (yearsToTarget(rec.target_date, refDate) == null) return 'No future target date';
  return 'Not available';
};

/** LTP against the recommended price, as a percentage. Null when either is missing. */
export const ltpVsRecPrice = (item: TradeSuggestionsItem, rec: Recommendation | null): number | null => {
  const decisionPrice = rec?.decision_price;
  if (item.ltp == null || !decisionPrice) return null;
  return ((item.ltp - decisionPrice) / decisionPrice) * 100;
};

/** Buy and add are positive, sell and reduce are negative. Anything else is
 *  neither, so it stays neutral rather than borrowing a colour. */
export const suggestionTone = (tradeType: string | null | undefined): PillTone => {
  const type = tradeType?.toLowerCase() ?? '';
  if (type.includes('buy') || type.includes('add')) return 'positive';
  if (type.includes('sell') || type.includes('reduce')) return 'critical';
  return 'neutral';
};

// ---- Search and sort -----------------------------------------------------

/** Free-text search across the company and its recommendations. Filters whole
 *  companies, so a match never strands a company's other recommendation rows. */
export const filterItems = (
  items: TradeSuggestionsItem[],
  searchTerm: string,
): TradeSuggestionsItem[] => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return items;
  return items.filter(item =>
    item.symbol.toLowerCase().includes(term) ||
    item.company_name.toLowerCase().includes(term) ||
    item.category?.toLowerCase().includes(term) ||
    item.recommendations.some(rec =>
      rec.recommended_by?.toLowerCase().includes(term) ||
      rec.recommendation_type?.toLowerCase().includes(term) ||
      rec.decision?.toLowerCase().includes(term),
    ),
  );
};

/** Sort value for one company under a given column key. Recommendation columns
 *  read the first recommendation, so every row of a company sorts together and
 *  its recommendations stay adjacent whatever the table is sorted by. */
const sortValue = (item: TradeSuggestionsItem, key: string, refDate: Date): string | number => {
  const firstRec = item.recommendations[0] ?? null;
  switch (key) {
    case 'symbol': return item.symbol;
    case 'company_name': return item.company_name;
    case 'ltp': return item.ltp ?? 0;
    case 'category': return item.category ?? '';
    case 'is_invested': return item.is_invested ? 1 : 0;
    case 'rec_date': return firstRec?.rec_date ? new Date(firstRec.rec_date).getTime() : 0;
    case 'recommended_by': return firstRec?.recommended_by ?? '';
    case 'recommendation_type': return firstRec?.recommendation_type ?? '';
    case 'decision': return firstRec?.decision ?? '';
    case 'decision_price': return firstRec?.decision_price ?? 0;
    case 'cmp_vs_model': return ltpVsRecPrice(item, firstRec) ?? 0;
    case 'target_weight': return firstRec?.target_weight ?? 0;
    case 'target_price': return firstRec?.target_price ?? 0;
    // -Infinity, not 0: an implied return can legitimately be negative, so blanks
    // must rank below every real figure rather than above the negative ones.
    case 'implied_return': return rowImpliedReturn(item, firstRec, refDate) ?? -Infinity;
    default: {
      if (!key.includes(STRATEGY_SORT_SEP)) return 0;
      const [strategyCode, holdingType] = key.split(STRATEGY_SORT_SEP);
      const strategy = item.strategies[strategyCode];
      if (holdingType === 'min') return strategy?.min_client_holding ?? 0;
      if (holdingType === 'max') return strategy?.max_client_holding ?? 0;
      if (holdingType === 'median') return strategy?.median_client_holding ?? 0;
      return 0;
    }
  }
};

export const sortItems = (
  items: TradeSuggestionsItem[],
  sortField: string,
  sortDir: 'asc' | 'desc',
  refDate: Date = new Date(),
): TradeSuggestionsItem[] => {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const aValue = sortValue(a, sortField, refDate);
    const bValue = sortValue(b, sortField, refDate);
    if (aValue < bValue) return -dir;
    if (aValue > bValue) return dir;
    return 0;
  });
};

// ---- Strategy metadata -----------------------------------------------------

/** True when any company holds this strategy across more than one client, which
 *  is what makes a min/median/max spread worth showing instead of one figure. */
export const hasMultipleClients = (data: TradeSuggestionsItem[], strategyCode: string): boolean =>
  data.some(item => (item.strategies[strategyCode]?.client_count ?? 0) > 1);

/** The strategy's AUM and client count, for the column header tooltip. */
export const strategyStats = (data: TradeSuggestionsItem[], strategyCode: string) => {
  for (const item of data) {
    const strategy = item.strategies[strategyCode];
    if (strategy) {
      return { totalAum: strategy.aum || 0, totalClients: strategy.client_count || 0 };
    }
  }
  return { totalAum: 0, totalClients: 0 };
};

/** Total recommendation rows across every company — a company with none still
 *  counts once, matching how the table gives it a single (empty) row. */
export const totalRecommendationRows = (items: TradeSuggestionsItem[]): number =>
  items.reduce((sum, item) => sum + Math.max(1, item.recommendations.length), 0);

/** Headline figures for the page header: how much of the table is a signal. */
export const summariseRows = (items: TradeSuggestionsItem[], strategyKeys: string[]) => {
  let buys = 0;
  let sells = 0;
  items.forEach(item => {
    item.recommendations.forEach(rec => {
      strategyKeys.forEach(strategyCode => {
        const tone = suggestionTone(rec.suggestions?.[strategyCode]?.trade_type);
        if (tone === 'positive') buys += 1;
        if (tone === 'critical') sells += 1;
      });
    });
  });
  return { buys, sells };
};
